'use client'

import * as React from 'react'
import { Editor } from '@tiptap/react'
import {
  Popover,
  PopoverContent,
  PopoverAnchor,
} from '@deepguide-ai/ui/popover'
import { Input } from '@deepguide-ai/ui/input'
import { Textarea } from '@deepguide-ai/ui/textarea'
import { Button } from '@deepguide-ai/ui/button'
import { Label } from '@deepguide-ai/ui/label'
import { Switch } from '@deepguide-ai/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import { AlertCircle, Trash2, X } from 'lucide-react'
import { trpcClient } from '@/lib/trpc'
import { isNativeIntegrationProvider } from '@/lib/action-pill-providers'

/**
 * Tool catalog is server-static within a session. Cache the in-flight
 * promise at module scope so opening the pill popover N times triggers
 * one network fetch instead of N. RunbookToolsSection already caches
 * via TanStack Query with a 5-minute staleTime; this keeps the picker
 * consistent without taking a hook dependency in the imperative
 * event-handler path.
 */
let toolCatalogPromise: ReturnType<
  typeof trpcClient.agentPermissions.getCatalog.query
> | null = null

function fetchToolCatalogCached() {
  if (!toolCatalogPromise) {
    toolCatalogPromise = trpcClient.agentPermissions.getCatalog
      .query()
      .catch((err) => {
        // Reset on failure so a transient network error doesn't
        // permanently break the picker for the rest of the session.
        toolCatalogPromise = null
        throw err
      })
  }
  return toolCatalogPromise
}

interface ActionPillManagerProps {
  editor: Editor
  readOnly?: boolean
}

type InputType = 'reference' | 'string' | 'boolean' | 'string[]'
interface InputField {
  name: string
  description: string
  required: boolean
  type: InputType
  placeholder?: string
}

function decodeBase64Utf8(base64: string): string {
  const binary = atob(base64)
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0))
  return new TextDecoder('utf-8').decode(bytes)
}

interface PillSchema {
  description: string | null
  inputs: InputField[]
}

/**
 * TEMPORARY adapter (centralization follow-up).
 *
 * The agent catalog now declares an action's `inputs` as
 * `ActionCatalogCheckVariable` ({ id, label, type: 'text'|'number'|'boolean'|
 * 'select'|'multi-select', … }), but this legacy tiptap pill editor renders the
 * older `InputField` model ({ name, type: 'reference'|'string'|'boolean'|
 * 'string[]' }). Without this map the catalog inputs would mis-render here
 * (`name` undefined → params written under an `undefined` key). So bridge the
 * two shapes for now.
 *
 * CLEAN FIX (do this when the remaining tiptap runbook surfaces — the test
 * Runbook tab, TestForm, and template detail — are migrated to the graph editor
 * like `/runbooks/$id` already is; at that point ActionPillManager is retired):
 * delete this adapter and render the shared
 * `@/components/compliance/VariableField` directly against
 * `ActionCatalogCheckVariable` (id-keyed, `reference` → the upstream-output
 * combobox, `string[]` → multi-select) — the SAME component the graph editor
 * and CheckVariablesDialog already use, so all three configure params one way.
 */
type CatalogInputVariable = {
  id: string
  type: 'text' | 'number' | 'boolean' | 'select' | 'multi-select'
  required?: boolean
  helpText?: string
  placeholder?: string
}

function catalogInputsToInputFields(inputs: unknown): InputField[] {
  if (!Array.isArray(inputs)) return []
  return inputs.map((raw) => {
    const v = raw as CatalogInputVariable
    const type: InputType =
      v.type === 'boolean'
        ? 'boolean'
        : v.type === 'multi-select'
          ? 'string[]'
          : 'string'
    return {
      // `name` is BOTH the displayed label and the param key the tool reads, so
      // it must be the variable id (the real param name), not a friendly label.
      name: v.id,
      description: v.helpText ?? '',
      required: v.required ?? false,
      type,
      ...(v.placeholder != null ? { placeholder: v.placeholder } : {}),
    }
  })
}

async function resolvePillSchema(attrs: Record<string, any>): Promise<PillSchema> {
  if (typeof attrs.tool === 'string' && attrs.tool.trim().length > 0) {
    const toolId = attrs.tool.trim()
    const [toolNamespace, toolAction] = toolId.split('.')
    if (!toolNamespace || !toolAction) return { description: null, inputs: [] }

    const toolCatalog = await trpcClient.agentPermissions.getCatalog.query()
    const tool = toolCatalog.tools.find((entry) => entry.name === toolNamespace)
    const action = tool?.actions.find((entry) => entry.name === toolAction)
    return {
      description: action?.description ?? null,
      inputs: catalogInputsToInputFields((action as any)?.inputs),
    }
  }

  // Provider-style action pills (no tool id) — fall back to legacy catalog;
  // input schema not declared on that catalog yet.
  const catalog = await trpcClient.actionCatalog.getAvailableActions.query()
  const checkId = attrs.checkId
  const providerKey = (attrs.provider || '').toLowerCase()
  const actionKey = attrs.action
  const prefixed = providerKey && checkId ? `${providerKey}.${checkId}` : null
  const byAction = providerKey && actionKey ? `${providerKey}.${actionKey}` : null

  for (const providerGroup of catalog.providers) {
    for (const act of providerGroup.actions) {
      if (
        (checkId && act.checkId === checkId) ||
        (prefixed && act.checkId === prefixed) ||
        (byAction && act.checkId === byAction) ||
        (providerKey &&
          providerGroup.provider === providerKey &&
          act.action === actionKey)
      ) {
        return { description: act.description, inputs: [] }
      }
    }
  }

  return { description: null, inputs: [] }
}

function formatParamForInput(value: unknown, type: InputType): string {
  if (value === undefined || value === null) return ''
  if (type === 'string[]' && Array.isArray(value)) return value.join(', ')
  if (type === 'boolean')
    return value === true ? 'true' : value === false ? 'false' : ''
  if (typeof value === 'string') return value
  return String(value)
}

function parseParamFromInput(raw: string, type: InputType): unknown {
  const trimmed = raw.trim()
  if (trimmed.length === 0) return undefined
  if (type === 'boolean') {
    if (trimmed === 'true') return true
    if (trimmed === 'false') return false
    return undefined
  }
  if (type === 'string[]') {
    return trimmed
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)
  }
  return raw
}

interface FieldEditorProps {
  field: InputField
  rawValue: unknown
  availableRefs: Array<{ ref: string; alias: string; stepLabel: string }>
  writeParam: (key: string, value: unknown) => void
}

const CUSTOM_REF_SENTINEL = '__custom__'

function FieldEditor({
  field,
  rawValue,
  availableRefs,
  writeParam,
}: FieldEditorProps) {
  const value = formatParamForInput(rawValue, field.type)
  const isReference = field.type === 'reference'

  const matchesKnownRef = isReference && availableRefs.some((r) => r.ref === value)
  const [refMode, setRefMode] = React.useState<'pick' | 'custom'>(() => {
    if (!isReference) return 'pick'
    if (!value) return availableRefs.length > 0 ? 'pick' : 'custom'
    return matchesKnownRef ? 'pick' : 'custom'
  })

  // When availableRefs hydrate after open, snap to picker if the current
  // value happens to match a known ref.
  React.useEffect(() => {
    if (!isReference) return
    if (refMode === 'pick' && value && !matchesKnownRef) setRefMode('custom')
    // intentional: only react to hydration changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableRefs.length, isReference])

  const labelEl = (
    <Label className="text-[11px] text-text-body">
      {field.name}
      {field.required && <span className="ml-0.5 text-status-red-emphasis">*</span>}
    </Label>
  )

  if (isReference) {
    return (
      <div>
        {labelEl}
        {refMode === 'pick' && availableRefs.length > 0 ? (
          <select
            className="mt-0.5 h-7 w-full rounded-md border border-border-divider bg-card px-2 text-[11px] text-text-secondary focus:outline-none focus:ring-1 focus:ring-ring"
            value={matchesKnownRef ? value : ''}
            onChange={(e) => {
              const next = e.target.value
              if (next === CUSTOM_REF_SENTINEL) {
                setRefMode('custom')
                return
              }
              writeParam(field.name, next || undefined)
            }}
          >
            <option value="" disabled>
              Pick a step output…
            </option>
            {availableRefs.map((opt) => (
              <option key={opt.ref} value={opt.ref}>
                @{opt.alias}
                {opt.stepLabel ? ` · ${opt.stepLabel}` : ''}
              </option>
            ))}
            <option value={CUSTOM_REF_SENTINEL}>Custom value…</option>
          </select>
        ) : (
          <div className="mt-0.5 flex gap-1">
            <Input
              className="h-7 flex-1 text-[11px]"
              placeholder={field.placeholder ?? '@step.<outputVar> or literal'}
              value={value}
              onChange={(e) =>
                writeParam(field.name, parseParamFromInput(e.target.value, field.type))
              }
            />
            {availableRefs.length > 0 && (
              <button
                type="button"
                className="text-[10px] text-cobalt-strong hover:underline"
                onClick={() => setRefMode('pick')}
              >
                pick
              </button>
            )}
          </div>
        )}
        {field.description && (
          <p className="mt-0.5 text-[9px] text-text-placeholder leading-snug">
            {field.description}
          </p>
        )}
      </div>
    )
  }

  if (field.type === 'boolean') {
    const checked = rawValue === true
    return (
      <div className="flex items-start justify-between gap-3 py-0.5">
        <div className="min-w-0 flex-1">
          <Label className="text-[11px] text-text-body">
            {field.name}
            {field.required && <span className="ml-0.5 text-status-red-emphasis">*</span>}
          </Label>
          {field.description && (
            <p className="mt-0.5 text-[9px] text-text-placeholder leading-snug">
              {field.description}
            </p>
          )}
        </div>
        <Switch
          checked={checked}
          onCheckedChange={(next) => writeParam(field.name, next)}
        />
      </div>
    )
  }

  return (
    <div>
      {labelEl}
      <Input
        className="mt-0.5 h-7 text-[11px]"
        placeholder={field.placeholder ?? ''}
        value={value}
        onChange={(e) =>
          writeParam(field.name, parseParamFromInput(e.target.value, field.type))
        }
      />
      {field.description && (
        <p className="mt-0.5 text-[9px] text-text-placeholder leading-snug">
          {field.description}
        </p>
      )}
    </div>
  )
}

/**
 * Row for a param that isn't declared in the tool's input schema. Most
 * runbooks should never hit this branch — proposeRunbook now strict-validates
 * action params at authoring time and rejects unknown keys. This component
 * exists to recover legacy persisted runbooks that still carry undeclared
 * params, and to let the user rename a key into a declared field's name (or
 * delete it entirely) without re-authoring the whole runbook.
 *
 * Local state for `keyDraft` so typing a new key name doesn't lose focus on
 * every keystroke (renaming the underlying param remounts the row because
 * its React key is the param key itself).
 */
interface UndeclaredParamRowProps {
  paramKey: string
  value: string
  declaredFieldNames: string[]
  onRename: (next: string) => void
  onChangeValue: (next: string) => void
  onRemove: () => void
}

function UndeclaredParamRow({
  paramKey,
  value,
  declaredFieldNames,
  onRename,
  onChangeValue,
  onRemove,
}: UndeclaredParamRowProps) {
  const [keyDraft, setKeyDraft] = React.useState(paramKey)
  React.useEffect(() => {
    setKeyDraft(paramKey)
  }, [paramKey])

  const renameSuggestion = React.useMemo(() => {
    if (declaredFieldNames.length === 0) return null
    const lowerKey = paramKey.toLowerCase()
    // Heuristic: if any declared field shares a prefix or is contained in
    // the undeclared key (e.g. "fileName" → "filename", "ticketBody" →
    // "description" only when explicitly aliased), surface as a hint.
    const exact = declaredFieldNames.find(
      (n) => n.toLowerCase() === lowerKey,
    )
    if (exact) return exact
    const partial = declaredFieldNames.find(
      (n) =>
        n.toLowerCase().startsWith(lowerKey) ||
        lowerKey.startsWith(n.toLowerCase()),
    )
    return partial ?? null
  }, [paramKey, declaredFieldNames])

  const commitKeyRename = () => {
    const trimmed = keyDraft.trim()
    if (!trimmed || trimmed === paramKey) {
      setKeyDraft(paramKey)
      return
    }
    onRename(trimmed)
  }

  return (
    <div>
      <div className="flex items-center gap-1.5">
        <Input
          className="h-6 flex-1 text-[11px] font-medium text-text-body"
          value={keyDraft}
          onChange={(e) => setKeyDraft(e.target.value)}
          onBlur={commitKeyRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              commitKeyRename()
            } else if (e.key === 'Escape') {
              e.preventDefault()
              setKeyDraft(paramKey)
            }
          }}
          spellCheck={false}
          aria-label={`Rename param ${paramKey}`}
        />
        <Button
          type="button"
          variant="ghost"
          size="xxsIcon"
          className="size-5 shrink-0 text-text-placeholder hover:text-red-500"
          onClick={onRemove}
          aria-label={`Remove param ${paramKey}`}
          title="Remove this parameter"
        >
          <X className="h-3 w-3" />
        </Button>
      </div>
      {renameSuggestion && renameSuggestion !== paramKey && (
        <button
          type="button"
          className="mt-0.5 text-[10px] text-cobalt-strong hover:underline"
          onClick={() => {
            setKeyDraft(renameSuggestion)
            onRename(renameSuggestion)
          }}
        >
          Rename to “{renameSuggestion}”
        </button>
      )}
      <Input
        className="mt-0.5 h-7 text-[11px]"
        value={value}
        onChange={(e) => onChangeValue(e.target.value)}
        placeholder="Value"
      />
    </div>
  )
}

// Walk the doc and surface every action-pill outputVar that appears *before*
// the pill being edited as a canonical `@step.<id>.<outputVar>` ref. Mirrors
// the shape used by RunbookDocumentEditor.getRefItems but scoped to step
// outputs (good enough for the popover's reference-type fields — params /
// run / env can be added later if requested).
function collectStepOutputRefs(
  editor: Editor,
  currentPillPos: number,
): Array<{ ref: string; alias: string; stepLabel: string }> {
  const out: Array<{ ref: string; alias: string; stepLabel: string }> = []
  editor.state.doc.descendants((node, pos) => {
    if (node.type.name !== 'actionPill') return true
    if (pos >= currentPillPos) return false
    const outputVar =
      typeof node.attrs?.outputVar === 'string' ? node.attrs.outputVar.trim() : ''
    if (!outputVar) return false

    const $pos = editor.state.doc.resolve(pos)
    let stepId = ''
    let stepLabel = ''
    for (let depth = $pos.depth; depth > 0; depth--) {
      const ancestor = $pos.node(depth)
      if (ancestor.type.name === 'listItem') {
        const ancestorAttrs: any = ancestor.attrs ?? {}
        stepId = typeof ancestorAttrs.stepId === 'string' ? ancestorAttrs.stepId : ''
        const text = ancestor.textContent.trim().split('\n')[0] ?? ''
        stepLabel = text || stepId
        break
      }
    }
    if (!stepId) return false

    out.push({
      ref: `@step.${stepId}.${outputVar}`,
      alias: outputVar,
      stepLabel,
    })
    return false
  })
  return out
}

/**
 * Manages the ActionPill config popover. Listens for the openActionPillEditor
 * event emitted by the ActionPill node view on click, and renders a form
 * popover for editing pill parameters and output variable.
 */
export function ActionPillManager({ editor, readOnly = false }: ActionPillManagerProps) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [pillAttrs, setPillAttrs] = React.useState<Record<string, any>>({})
  const [nodePos, setNodePos] = React.useState<number | null>(null)
  const [anchorRect, setAnchorRect] = React.useState<DOMRect | null>(null)
  const [checkDescription, setCheckDescription] = React.useState<string | null>(null)
  const [declaredInputs, setDeclaredInputs] = React.useState<InputField[]>([])
  const [showAdvanced, setShowAdvanced] = React.useState(false)
  const [availableRefs, setAvailableRefs] = React.useState<
    Array<{ ref: string; alias: string; stepLabel: string }>
  >([])
  // RFC #323: connection picker. Loaded asynchronously per pill provider
  // when the popover opens; only rendered when 2+ matching connections
  // exist (single-connection providers don't need a picker).
  const [connectionOptions, setConnectionOptions] = React.useState<
    Array<{ id: string; name: string; integrationType: string }>
  >([])
  // RFC #323 connection-binding mode for the pill being edited.
  // 'none' = picker hidden; 'optional' = picker shown, no validation;
  // 'required-when-multiple' = picker shown + validation when 2+
  // connections exist with no binding.
  const [connectionMode, setConnectionMode] = React.useState<
    'none' | 'optional' | 'required-when-multiple'
  >('none')

  React.useEffect(() => {
    const handleOpen = ({ pos }: { pos?: number } = {}) => {
      if (pos == null) return
      const targetNode = editor.state.doc.nodeAt(pos)
      if (!targetNode || targetNode.type.name !== 'actionPill') return

      const attrs = { ...targetNode.attrs }
      setPillAttrs(attrs)
      setNodePos(pos)
      setCheckDescription(null)
      setDeclaredInputs([])
      setShowAdvanced(false)
      setAvailableRefs(collectStepOutputRefs(editor, pos))
      setConnectionOptions([])
      setConnectionMode('none')

      void resolvePillSchema(attrs)
        .then(({ description, inputs }) => {
          setCheckDescription(description)
          setDeclaredInputs(inputs)
        })
        .catch((err) => {
          console.warn('[ActionPillManager] Failed to fetch pill schema:', err)
        })

      // Resolve the provider whose connections to load. Two paths:
      //  1. Native-adapter pills (`provider: 'aws' | 'github' | …`):
      //     load connections directly; mode is 'optional'.
      //  2. Tool pills (`tool: 'population.listUsers'`): look up the
      //     catalog entry; load connections only when the catalog
      //     declares a connectionMode, using the catalog's providers[0].
      const rawProvider = (attrs.provider || '').toLowerCase()
      const toolId = (attrs.tool || '').trim()
      const isNativeProvider = isNativeIntegrationProvider({
        provider: rawProvider,
        tool: toolId,
      })

      const loadConnectionsForProvider = (provider: string) => {
        void trpcClient.actionCatalog.listProviderConnections
          .query({ provider })
          .then((rows) => setConnectionOptions(rows))
          .catch((err) => {
            console.warn(
              '[ActionPillManager] Failed to load connections for provider:',
              provider,
              err,
            )
          })
      }

      if (isNativeProvider) {
        setConnectionMode('optional')
        loadConnectionsForProvider(rawProvider)
      } else if (toolId) {
        const [namespace, actionName] = toolId.split('.')
        void fetchToolCatalogCached()
          .then((catalog) => {
            const tool = catalog.tools.find((t) => t.name === namespace)
            const action = tool?.actions.find((a) => a.name === actionName)
            const mode = action?.connectionMode
            const providers = action?.providers ?? []
            if (mode && providers.length === 1) {
              setConnectionMode(mode)
              loadConnectionsForProvider(providers[0])
            }
          })
          .catch((err) => {
            console.warn(
              '[ActionPillManager] Failed to load tool catalog for connection picker:',
              err,
            )
          })
      }

      // Find the pill's DOM element to anchor the popover
      try {
        const domNode = editor.view.nodeDOM(pos)
        if (domNode instanceof HTMLElement) {
          setAnchorRect(domNode.getBoundingClientRect())
        } else {
          const coords = editor.view.coordsAtPos(pos)
          setAnchorRect(new DOMRect(coords.left, coords.top, 0, coords.bottom - coords.top))
        }
      } catch {
        setAnchorRect(null)
      }

      setIsOpen(true)
    }

    editor.on('openActionPillEditor', handleOpen)
    return () => {
      editor.off('openActionPillEditor', handleOpen)
    }
  }, [editor])

  const updateAttr = (key: string, value: string) => {
    setPillAttrs((prev) => ({ ...prev, [key]: value }))
    if (nodePos != null) {
      const currentNode = editor.state.doc.nodeAt(nodePos)
      const currentAttrs = currentNode?.attrs ?? pillAttrs
      const tr = editor.state.tr.setNodeMarkup(nodePos, undefined, {
        ...currentAttrs,
        [key]: value,
      })
      editor.view.dispatch(tr)
    }
  }

  const handleDelete = () => {
    if (nodePos != null) {
      editor
        .chain()
        .focus()
        .setNodeSelection(nodePos)
        .deleteSelection()
        .run()
    }
    setIsOpen(false)
  }

  const isAiPill =
    (pillAttrs.provider || '').toLowerCase() === 'ai' ||
    pillAttrs.tool === 'ai.reason' ||
    (pillAttrs.provider || '').toLowerCase() === 'tool:ai'

  const parsedParams = React.useMemo(() => {
    try {
      if (typeof pillAttrs.params !== 'string') return pillAttrs.params || {}
      const raw = pillAttrs.params
      // Handle base64-encoded params (from MDX serialization)
      if (raw.startsWith('base64:')) {
        const decoded = decodeBase64Utf8(raw.slice(7))
        return JSON.parse(decoded)
      }
      return JSON.parse(raw)
    } catch {
      return {}
    }
  }, [pillAttrs.params])

  // Filter out 'prompt' from params display since it gets its own field for AI pills
  const nonPromptParams = React.useMemo(() => {
    if (!isAiPill) return parsedParams
    const { prompt: _, ...rest } = parsedParams
    return rest
  }, [parsedParams, isAiPill])

  // Extra params present on the pill but not declared in the catalog input
  // schema. Shown after declared inputs so legacy / hand-edited params still
  // surface for editing.
  const undeclaredParams = React.useMemo(() => {
    const declaredNames = new Set(declaredInputs.map((field) => field.name))
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(nonPromptParams)) {
      if (!declaredNames.has(key)) out[key] = value
    }
    return out
  }, [nonPromptParams, declaredInputs])

  const writeParam = React.useCallback(
    (key: string, value: unknown) => {
      const next = { ...parsedParams }
      if (value === undefined || value === '' || value === null) {
        delete next[key]
      } else {
        next[key] = value
      }
      updateAttr('params', JSON.stringify(next))
    },
    // updateAttr is stable enough — it reads pillAttrs from state by closure;
    // re-binding on parsedParams keeps the closure fresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parsedParams],
  )

  const renameParam = React.useCallback(
    (oldKey: string, newKey: string) => {
      const trimmed = newKey.trim()
      if (!trimmed || trimmed === oldKey) return
      // Preserve insertion order so the renamed slot stays where the user
      // sees it instead of jumping to the end.
      const next: Record<string, unknown> = {}
      for (const [k, v] of Object.entries(parsedParams)) {
        next[k === oldKey ? trimmed : k] = v
      }
      updateAttr('params', JSON.stringify(next))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parsedParams],
  )

  const removeParam = React.useCallback(
    (key: string) => {
      const next = { ...parsedParams }
      delete next[key]
      updateAttr('params', JSON.stringify(next))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parsedParams],
  )

  const { requiredInputs, optionalInputs } = React.useMemo(() => {
    const req: InputField[] = []
    const opt: InputField[] = []
    for (const field of declaredInputs) {
      ;(field.required ? req : opt).push(field)
    }
    return { requiredInputs: req, optionalInputs: opt }
  }, [declaredInputs])

  // Virtual anchor ref that Radix uses for positioning
  const virtualRef = React.useRef<HTMLSpanElement>(null)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverAnchor asChild>
        <span
          ref={virtualRef}
          style={
            anchorRect
              ? {
                  position: 'fixed',
                  left: anchorRect.left,
                  top: anchorRect.bottom,
                  width: anchorRect.width || 1,
                  height: 1,
                  pointerEvents: 'none',
                }
              : { display: 'none' }
          }
        />
      </PopoverAnchor>
      <PopoverContent
        className="w-72 p-0"
        side="bottom"
        align="start"
        sideOffset={4}
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="border-b border-border bg-surface-subtle px-3 py-2">
          <div className="text-[11px] font-medium text-cobalt-strong">
            {pillAttrs.label ||
              `${(pillAttrs.provider || '').toUpperCase()}: ${pillAttrs.action || ''}`}
          </div>
          {pillAttrs.checkId && (
            <div className="mt-0.5 text-[10px] text-text-placeholder">
              {pillAttrs.checkId}
            </div>
          )}
          {checkDescription && (
            <div className="mt-1 text-[10px] text-muted-foreground leading-relaxed">
              {checkDescription}
            </div>
          )}
        </div>

        {readOnly ? (
          <div className="space-y-2 px-3 py-3">
            {isAiPill && parsedParams.prompt && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                  Prompt
                </Label>
                <div className="mt-0.5 text-[11px] text-text-body leading-relaxed line-clamp-3">
                  {parsedParams.prompt}
                </div>
              </div>
            )}
            {pillAttrs.outputVar && (
              <div>
                <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                  Output Variable
                </Label>
                <div className="mt-0.5 text-[11px] text-text-secondary">
                  {pillAttrs.outputVar}
                </div>
              </div>
            )}
            {pillAttrs.connectionId && (() => {
              const conn = connectionOptions.find(
                (opt) => opt.id === pillAttrs.connectionId,
              )
              return (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                    Connection
                  </Label>
                  <div className="mt-0.5 text-[11px] text-text-secondary">
                    {conn?.name ?? pillAttrs.connectionId}
                    {!conn && (
                      <span className="ml-1 text-[10px] text-status-red-deep">
                        (unavailable)
                      </span>
                    )}
                  </div>
                </div>
              )
            })()}
            {(() => {
              const declaredWithValue = declaredInputs.filter((field) => {
                const raw = (parsedParams as Record<string, unknown>)[field.name]
                return raw !== undefined && raw !== null && raw !== ''
              })
              const undeclaredEntries = Object.entries(undeclaredParams)
              if (declaredWithValue.length === 0 && undeclaredEntries.length === 0) {
                return null
              }
              return (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                    Parameters
                  </Label>
                  {declaredWithValue.map((field) => {
                    const raw = (parsedParams as Record<string, unknown>)[field.name]
                    const display = formatParamForInput(raw, field.type)
                    return (
                      <div key={field.name} className="mt-0.5 text-[11px] text-text-body">
                        <span className="font-medium">{field.name}:</span> {display}
                      </div>
                    )
                  })}
                  {undeclaredEntries.map(([key, value]) => (
                    <div key={key} className="mt-0.5 text-[11px] text-text-body">
                      <span className="font-medium">{key}:</span> {String(value ?? '')}
                    </div>
                  ))}
                </div>
              )
            })()}
          </div>
        ) : (
          <>
            <div className="space-y-3 px-3 py-3">
              {isAiPill && (
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                    Prompt
                  </Label>
                  <Textarea
                    className="mt-0.5 text-[11px] min-h-[80px]"
                    placeholder="Describe what the AI should analyze or generate..."
                    value={parsedParams.prompt || ''}
                    onChange={(e) => {
                      const newParams = { ...parsedParams, prompt: e.target.value }
                      updateAttr('params', JSON.stringify(newParams))
                    }}
                  />
                </div>
              )}
              {declaredInputs.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                    Parameters
                  </Label>
                  {requiredInputs.map((field) => (
                    <FieldEditor
                      key={field.name}
                      field={field}
                      rawValue={(parsedParams as Record<string, unknown>)[field.name]}
                      availableRefs={availableRefs}
                      writeParam={writeParam}
                    />
                  ))}
                  {optionalInputs.length > 0 && (
                    <div className="pt-1">
                      <button
                        type="button"
                        className="text-[10px] uppercase tracking-wider text-cobalt-strong hover:text-[#1A2D7A]"
                        onClick={() => setShowAdvanced((v) => !v)}
                      >
                        {showAdvanced ? '− Hide advanced' : '+ Show advanced'}
                      </button>
                    </div>
                  )}
                  {showAdvanced &&
                    optionalInputs.map((field) => (
                      <FieldEditor
                        key={field.name}
                        field={field}
                        rawValue={(parsedParams as Record<string, unknown>)[field.name]}
                        availableRefs={availableRefs}
                        writeParam={writeParam}
                      />
                    ))}
                </div>
              )}
              {Object.keys(undeclaredParams).length > 0 && (
                <div className="space-y-2">
                  {declaredInputs.length === 0 && (
                    <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                      Parameters
                    </Label>
                  )}
                  {Object.entries(undeclaredParams).map(([key, value]) => (
                    <UndeclaredParamRow
                      key={key}
                      paramKey={key}
                      value={String(value ?? '')}
                      declaredFieldNames={declaredInputs.map((f) => f.name)}
                      onRename={(next) => renameParam(key, next)}
                      onChangeValue={(next) => writeParam(key, next)}
                      onRemove={() => removeParam(key)}
                    />
                  ))}
                </div>
              )}

              {/* RFC #323: Connection picker. Renders when the pill's
                  connectionMode is non-'none' AND 2+ connections exist
                  for the provider — single-connection workspaces don't
                  need to disambiguate. Tool pills opt in via the
                  catalog's `connectionMode` field. */}
              {connectionMode !== 'none' &&
                connectionOptions.length >= 2 &&
                (() => {
                  const boundId = pillAttrs.connectionId || ''
                  const boundExists =
                    boundId === '' ||
                    connectionOptions.some((opt) => opt.id === boundId)
                  const requiresBinding =
                    connectionMode === 'required-when-multiple'
                  const missingRequiredBinding = requiresBinding && boundId === ''
                  return (
                    <div>
                      <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                        Connection
                        {requiresBinding && (
                          <span className="ml-1 text-status-red-deep">*</span>
                        )}
                      </Label>
                      <Select
                        value={boundId || '__any__'}
                        onValueChange={(value) =>
                          updateAttr(
                            'connectionId',
                            value === '__any__' ? '' : value,
                          )
                        }
                      >
                        <SelectTrigger
                          className={`mt-0.5 h-7 text-[11px] ${
                            missingRequiredBinding
                              ? 'border-status-red-border-subtle bg-status-red-surface-subtle'
                              : ''
                          }`}
                        >
                          <SelectValue placeholder="Any connected account" />
                        </SelectTrigger>
                        <SelectContent>
                          {!requiresBinding && (
                            <SelectItem value="__any__">
                              Any connected account
                            </SelectItem>
                          )}
                          {connectionOptions.map((opt) => (
                            <SelectItem key={opt.id} value={opt.id}>
                              {opt.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {missingRequiredBinding && (
                        <div className="mt-1 flex items-start gap-1 rounded-sm border border-status-red-border-subtle bg-status-red-surface-subtle px-2 py-1 text-[10px] text-status-red-deep">
                          <AlertCircle className="mt-0.5 h-2.5 w-2.5 flex-shrink-0" />
                          <span>
                            This action targets a single account. Pick one to
                            avoid an ambiguous audit trail.
                          </span>
                        </div>
                      )}
                      {!boundExists && (
                        <div className="mt-1 flex items-start gap-1 rounded-sm border border-status-red-border-subtle bg-status-red-surface-subtle px-2 py-1 text-[10px] text-status-red-deep">
                          <AlertCircle className="mt-0.5 h-2.5 w-2.5 flex-shrink-0" />
                          <span>
                            Bound connection no longer exists. Pick a new one
                            {!requiresBinding ? ' or clear the binding' : ''}.
                          </span>
                        </div>
                      )}
                      <p className="mt-0.5 text-[9px] text-text-placeholder">
                        {requiresBinding
                          ? 'This action requires an explicit account binding.'
                          : 'Pin this pill to a specific account, or leave as "Any" to use the run-level connection.'}
                      </p>
                    </div>
                  )
                })()}

              <div>
                <Label className="text-[10px] uppercase tracking-wider text-text-subtle">
                  Output Variable
                </Label>
                <Input
                  className="mt-0.5 h-7 text-[11px]"
                  placeholder="e.g., users"
                  value={pillAttrs.outputVar || ''}
                  onChange={(e) => updateAttr('outputVar', e.target.value)}
                />
                <p className="mt-0.5 text-[9px] text-text-placeholder">
                  Store this action's result for later @ references.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between border-t border-border px-3 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-6 gap-1 text-[10px] text-destructive hover:text-destructive"
                onClick={handleDelete}
              >
                <Trash2 className="h-3 w-3" />
                Remove
              </Button>
              <Button
                size="sm"
                className="h-6 text-[10px]"
                onClick={() => setIsOpen(false)}
              >
                Done
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
